/**
 * 공지사항 + 주간보고 API 서비스
 */
import { api } from './client';

// MYB-442: Manager digest response type
export interface ManagerDigestResult {
  week_start: string;
  digest: string;
  submitted_count: number;
  not_submitted_count: number;
  not_submitted_names: string[];
  from_cache: boolean;
  generated_at: number;
}

export const noticesApi = {
  // ── 공지사항 / 게시판 ──

  getNotices: async (params?: {
    post_type?: string;
    category?: string;
    limit?: number;
    offset?: number;
  }): Promise<any> => {
    const resp = await api.get('/api/v1/notices/list', { params });
    return resp.data;
  },

  getNotice: async (id: number): Promise<any> => {
    const resp = await api.get(`/api/v1/notices/${id}`);
    return resp.data;
  },

  createNotice: async (data: {
    title: string;
    content: string;
    post_type: string;
    category: string;
    target_audience?: string;
    is_pinned?: boolean;
  }): Promise<any> => {
    const resp = await api.post('/api/v1/notices/create', data);
    return resp.data;
  },

  updateNotice: async (id: number, data: {
    title?: string;
    content?: string;
    category?: string;
    target_audience?: string;
    is_pinned?: boolean;
  }): Promise<any> => {
    const resp = await api.put(`/api/v1/notices/${id}`, data);
    return resp.data;
  },

  deleteNotice: async (id: number): Promise<any> => {
    const resp = await api.delete(`/api/v1/notices/${id}`);
    return resp.data;
  },

  getReadStats: async (id: number): Promise<any> => {
    const resp = await api.get(`/api/v1/notices/${id}/read-stats`);
    return resp.data;
  },

  getUnreadCount: async (): Promise<any> => {
    const resp = await api.get('/api/v1/notices/unread-count');
    return resp.data;
  },

  // ── 주간보고 ──

  getMyReports: async (limit?: number): Promise<any> => {
    const resp = await api.get('/api/v1/weekly-reports/my', { params: { limit } });
    return resp.data;
  },

  getTeamReports: async (weekStart?: string): Promise<any> => {
    const resp = await api.get('/api/v1/weekly-reports/team', { params: { week_start: weekStart } });
    return resp.data;
  },

  getOrgSummary: async (
    weekStart?: string,
    includeDepartments: boolean = true,
    includeTrend: boolean = true,
    trendWeeks: number = 8,
  ): Promise<any> => {
    const resp = await api.get('/api/v1/weekly-reports/summary', {
      params: {
        week_start: weekStart,
        include_departments: includeDepartments,
        include_trend: includeTrend,
        trend_weeks: trendWeeks,
      },
    });
    return resp.data;
  },

  getNonSubmitters: async (weekStart?: string, orgUnitId?: number): Promise<any> => {
    const resp = await api.get('/api/v1/weekly-reports/non-submitters', {
      params: { week_start: weekStart, org_unit_id: orgUnitId },
    });
    return resp.data;
  },

  getCurrentWeek: async (): Promise<any> => {
    const resp = await api.get('/api/v1/weekly-reports/current-week');
    return resp.data;
  },

  createReport: async (data: {
    content: any;
    org_unit_id?: number;
    // MYB-389: structured escalation fields
    escalation_enabled?: boolean;
    escalation_type?: 'urgent' | 'blocker' | 'risk' | null;
    escalation_text?: string | null;
    escalation_visibility?: 'manager_only' | 'division' | 'all';
  }): Promise<any> => {
    const resp = await api.post('/api/v1/weekly-reports/create', data);
    return resp.data;
  },

  updateReport: async (
    id: number,
    data: {
      content?: any;
      escalation_enabled?: boolean;
      escalation_type?: 'urgent' | 'blocker' | 'risk' | null;
      escalation_text?: string | null;
      escalation_visibility?: 'manager_only' | 'division' | 'all';
    },
  ): Promise<any> => {
    const resp = await api.put(`/api/v1/weekly-reports/${id}`, data);
    return resp.data;
  },

  submitReport: async (id: number): Promise<any> => {
    const resp = await api.post(`/api/v1/weekly-reports/${id}/submit`);
    return resp.data;
  },

  reviewReport: async (id: number, data: { comment?: string }): Promise<any> => {
    const resp = await api.post(`/api/v1/weekly-reports/${id}/review`, data);
    return resp.data;
  },

  getReport: async (id: number): Promise<any> => {
    const resp = await api.get(`/api/v1/weekly-reports/${id}`);
    return resp.data;
  },

  // ── 집계 보고서 (Aggregate Reports) ──

  generateAggregate: async (data: {
    org_unit_id: number;
    week_start?: string;
    level: 'team' | 'division' | 'executive';
  }): Promise<any> => {
    const resp = await api.post('/api/v1/aggregate-reports/generate', data);
    return resp.data;
  },

  listAggregates: async (params?: {
    level?: string;
    week_start?: string;
    org_unit_id?: number;
  }): Promise<any> => {
    const resp = await api.get('/api/v1/aggregate-reports', { params });
    return resp.data;
  },

  getAggregate: async (id: number): Promise<any> => {
    const resp = await api.get(`/api/v1/aggregate-reports/${id}`);
    return resp.data;
  },

  getAggregateSources: async (id: number): Promise<any> => {
    const resp = await api.get(`/api/v1/aggregate-reports/${id}/sources`);
    return resp.data;
  },

  updateAggregate: async (id: number, final_content_json: any): Promise<any> => {
    const resp = await api.put(`/api/v1/aggregate-reports/${id}`, { final_content_json });
    return resp.data;
  },

  regenerateAggregate: async (id: number, force: boolean = false): Promise<any> => {
    const resp = await api.post(`/api/v1/aggregate-reports/${id}/regenerate`, { force });
    return resp.data;
  },

  submitAggregate: async (id: number): Promise<any> => {
    const resp = await api.post(`/api/v1/aggregate-reports/${id}/submit`);
    return resp.data;
  },

  // ── MYB-442: 매니저 주간 다이제스트 ──

  getManagerDigest: async (week?: string, refresh?: boolean): Promise<ManagerDigestResult> => {
    const resp = await api.get('/api/v1/weekly-reports/manager-digest', {
      params: { week, refresh },
    });
    return resp.data as ManagerDigestResult;
  },
};
